import type { Prisma } from "encamp-prisma/generated/browser";

import { parseAuditDecimal } from "encamp-shared/src/waste/invoiceAudit/decimal";
import {
  AUDIT_DECIMAL_PRECISION,
  type AuditMoneyColumn,
} from "encamp-shared/src/waste/invoiceAudit/precision";
import { WasteInvoiceChargeAuditResult } from "generated-graphql/graphql";

export const AUDIT_RESULT_LABELS: Record<WasteInvoiceChargeAuditResult, string> = {
  MATCH: "Match",
  RATE_MISMATCH: "Rate mismatch",
  UNRESOLVED: "Unresolved inputs",
};

function isDecimalString(value: string): value is `${number}` {
  return /^[+-]?\d+(?:\.\d+)?(?:e[+-]?\d+)?$/i.test(value);
}

export function formatMoney(
  amount: InstanceType<typeof Prisma.Decimal>,
  precision: number
): string {
  const value = amount.toFixed();
  if (!isDecimalString(value)) return "—";

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: precision,
    roundingMode: "halfExpand",
    signDisplay: "negative",
  }).format(value);
}

export function formatAuditMoney(
  column: AuditMoneyColumn,
  value: string | null | undefined
): string {
  const amount = parseAuditDecimal(value);
  if (amount == null) return "—";

  return formatMoney(amount, AUDIT_DECIMAL_PRECISION[column]);
}

export function getOverageAmount(variance: string | null | undefined): string | null {
  const decimal = parseAuditDecimal(variance);
  return decimal == null ? null : decimal.gt(0) ? decimal.toFixed() : "0";
}

export function formatIssueLabel(code: string): string {
  return code
    .toLowerCase()
    .replaceAll("_", " ")
    .replace(/^./, (letter) => letter.toUpperCase());
}

export function getUnderageAmount(variance: string | null | undefined): string | null {
  const decimal = parseAuditDecimal(variance);
  return decimal == null ? null : decimal.lt(0) ? decimal.abs().toFixed() : "0";
}

export function formatUnitRate(
  value: string | null | undefined,
  unit: string | null | undefined
): string {
  if (value == null) return "—";
  const rateUnit = unit?.trim();
  const label = rateUnit?.toUpperCase() === "LBS" ? "LB" : rateUnit;
  return `${formatAuditMoney("rateAmount", value)}/${label || "unit"}`;
}
